import { Link } from "react-router-dom";
import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ExternalLink,
  Users,
  ScrollText,
  Database,
  Ticket,
} from "lucide-react";
import { Pages, Routes } from "@/constants/enums";
import { appPath } from "@/constants/paths";
import { getCmsUrl } from "@/lib/app-config";
import { openCmsHandoff, openTicketingHandoff } from "@/lib/sso-handoff";
import { useUserPermissions } from "@/hooks/useUserPermissions";

interface QuickAction {
  label: string;
  icon: typeof Users;
  href?: string;
  external?: boolean;
  onClick?: () => void | Promise<void>;
  requiresSupportAccess?: boolean;
  /** The permission the destination route is guarded on — hide the shortcut without it. */
  permission?: string;
}

const actions: QuickAction[] = [
  {
    label: "نظام إدارة المحتوى (CMS)",
    icon: Database,
    onClick: () => void openCmsHandoff("/", `${getCmsUrl()}/`),
    external: true,
    permission: "content:edit",
  },
  {
    label: "نظام التذاكر",
    icon: Ticket,
    onClick: () => void openTicketingHandoff(),
    external: true,
    requiresSupportAccess: true,
  },
  {
    label: "المستخدمون",
    icon: Users,
    href: appPath(Pages.USERS),
    permission: "users:read",
  },
  {
    label: "سجل التدقيق",
    icon: ScrollText,
    href: appPath(Routes.AUDIT_LOGS),
    permission: "system:view-audit-logs",
  },
];

export function QuickActionsWidget() {
  const [cmsBusy, setCmsBusy] = useState(false);
  const { canAccessSupport, has } = useUserPermissions();
  const visibleActions = actions.filter(
    (action) =>
      (!action.requiresSupportAccess || canAccessSupport) &&
      (!action.permission || has(action.permission)),
  );

  return (
    <Card className="overflow-hidden" dir="rtl">
      <div className="accent-top-bar" />
      <CardHeader className="pb-2">
        <CardTitle className="text-base">إجراءات سريعة</CardTitle>
      </CardHeader>

      <CardContent className="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2">
        {visibleActions.map((action) => {
          const Icon = action.icon;
          const buttonClassName =
            "group h-auto w-full min-w-0 shrink items-center justify-start gap-2 whitespace-normal px-3 py-2.5 text-right";
          const inner = (
            <>
              <div className="shrink-0 rounded-md bg-primary/10 p-1.5 text-primary transition-colors group-hover:bg-primary-foreground/15 group-hover:text-primary-foreground">
                <Icon className="size-4" />
              </div>
              <span className="min-w-0 flex-1 leading-snug">{action.label}</span>
              {action.external && (
                <ExternalLink className="size-3 shrink-0 text-muted-foreground transition-colors group-hover:text-primary-foreground/80" />
              )}
            </>
          );

          if (action.onClick) {
            return (
              <Button
                key={action.label}
                variant="outline"
                className={buttonClassName}
                disabled={cmsBusy}
                onClick={() => {
                  setCmsBusy(true);
                  void Promise.resolve(action.onClick?.()).finally(() =>
                    setCmsBusy(false),
                  );
                }}
              >
                {inner}
              </Button>
            );
          }

          return action.external ? (
            <Button
              key={action.label}
              variant="outline"
              className={buttonClassName}
              asChild
            >
              <a href={action.href} target="_blank" rel="noopener noreferrer">
                {inner}
              </a>
            </Button>
          ) : (
            <Button
              key={action.label}
              variant="outline"
              className={buttonClassName}
              asChild
            >
              <Link to={action.href!}>{inner}</Link>
            </Button>
          );
        })}
      </CardContent>
    </Card>
  );
}
